import { useEffect } from 'react';
import { COACHING_ASTRO_DISCLAIMER, COACHING_FREQUENCY_DISCLAIMER } from '@mlm/shared/coaching';
import { ErrorNote, Eyebrow, Loading } from '../components/primitives';
import {
  ActionCard,
  AffirmationCard,
  CommunityRow,
  PhoneLinks,
  ProgramJourneyCard,
  RitualChips,
  RitualTiles,
  ScoreCard,
} from '../components/home/act';
import {
  BreatheRail,
  FrequenciesRail,
  LearnRail,
  ManifestTiles,
  MantrasRail,
  MeditateRail,
  PracticeColumns,
  TodayToolsRail,
  ToolkitRail,
} from '../components/home/collections';
import { ReadingCard } from '../components/home/reading-card';
import { coaching } from '../lib/api';
import { greeting, shortDay } from '../lib/format';
import { useActiveProgram, useEntitlement, useProfile, useToday } from '../lib/queries';
import { useHomeLibraries } from '../lib/use-home-libraries';
import { useIsDesktop } from '../lib/use-is-desktop';

/**
 * SC-13, the daily command center, in the 2026-10 "Daylight cards" redesign:
 * the reading first, then the tools it recommends, then the one action that
 * moves the day. Desktop shows the same parts as a dashboard beside the
 * sidebar.
 */
export function HomePage() {
  const today = useToday();
  const desktop = useIsDesktop();
  const profile = useProfile();
  const entitlement = useEntitlement();
  const activeProgram = useActiveProgram();
  const libraries = useHomeLibraries();

  useEffect(() => {
    coaching.event('home_viewed');
  }, []);

  if (today.isPending) return <Loading />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  const t = today.data;
  const minutes = profile.data?.profile.ritualPrefs.minutes ?? null;
  const community = entitlement.data?.community ?? null;
  const title = `${greeting()}${t.profile.firstName ? `, ${t.profile.firstName}` : ''}`;
  const program = activeProgram?.enrollment ? activeProgram : null;
  const astroNote = t.disclaimer ?? COACHING_ASTRO_DISCLAIMER;

  if (desktop) {
    return (
      <div className="w-full max-w-[1120px]">
        <header className="flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <Eyebrow>{shortDay(t.day)}</Eyebrow>
            <h1 className="mt-2 font-display text-[40px] font-medium leading-[1.05] tracking-[-0.015em] text-ink">
              {title}
            </h1>
            <p className="mt-2 text-[15px] text-ink/65">Ready to make today count?</p>
          </div>
          <RitualChips today={t} />
        </header>

        <ReadingCard today={t} layout="desktop" />

        <div className="mt-4 grid gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
          <ScoreCard today={t} layout="desktop" />
          <ActionCard today={t} layout="desktop" />
        </div>

        <FrequenciesRail libraries={libraries} layout="desktop" />
        <BreatheRail libraries={libraries} layout="desktop" />

        <div
          className={
            program
              ? 'mt-[22px] grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]'
              : 'mt-[22px] max-w-[560px]'
          }
        >
          {program && <ProgramJourneyCard program={program} layout="desktop" />}
          <AffirmationCard today={t} layout="desktop" />
        </div>

        <ManifestTiles layout="desktop" />
        <PracticeColumns libraries={libraries} />
        <CommunityRow community={community} />

        <p className="mt-[26px] max-w-[1080px] text-xs leading-normal text-sage-deep">
          {astroNote} {COACHING_FREQUENCY_DISCLAIMER}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl pb-7">
      <section className="px-5 pt-[22px]">
        <Eyebrow>{shortDay(t.day)}</Eyebrow>
        <h1 className="mt-1.5 font-display text-[30px] font-medium leading-[1.08] tracking-[-0.01em] text-ink">
          {title}
        </h1>
        <p className="mt-1.5 text-[15px] text-ink/65">Ready to make today count?</p>
      </section>

      <ReadingCard today={t} layout="phone" />

      <TodayToolsRail today={t} libraries={libraries} />
      <BreatheRail libraries={libraries} layout="phone" />
      <FrequenciesRail libraries={libraries} layout="phone" />
      <MeditateRail libraries={libraries} />
      <MantrasRail libraries={libraries} />
      <ToolkitRail libraries={libraries} />

      <div className="px-5 pt-[30px]">
        <p className="eyebrow text-[0.62rem]">Then act</p>
        <h2 className="mt-1.5 font-display text-[22px] font-medium leading-[1.2] text-ink">
          One action moves the day
        </h2>
      </div>
      <ActionCard today={t} layout="phone" />
      <ScoreCard today={t} layout="phone" />
      <RitualTiles today={t} minutes={minutes} />
      {program && <ProgramJourneyCard program={program} layout="phone" />}
      <ManifestTiles layout="phone" />
      <LearnRail />
      <AffirmationCard today={t} layout="phone" />
      <PhoneLinks today={t} community={community} />

      <p className="px-5 pt-[22px] text-[11px] leading-normal text-sage-deep">{astroNote}</p>
    </div>
  );
}
